import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Bell } from "lucide-react";

export function NotificationsList({ notifications, setNotifications, authApi }) {
  const markAllRead = async () => {
    await authApi().patch("/admin/notifications/read-all");
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={markAllRead} className="text-xs text-[#13A8BD] font-bold uppercase tracking-wider hover:text-[#0B2545]">Mark All Read</button>
      </div>
      <div className="bg-white border border-[#0B2545]/10">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No notifications.</div>
        ) : (
          notifications.map((n) => (
            <div key={n.id} className={`p-4 border-b border-[#0B2545]/10 last:border-b-0 ${!n.read ? 'bg-blue-50/30' : ''}`}>
              <div className="flex items-center gap-2 mb-1">
                <Bell size={12} className={n.read ? "text-slate-300" : "text-[#13A8BD]"} />
                <span className="text-xs font-bold text-[#0B2545]">{n.title}</span>
                <span className="text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                {!n.read && <span className="w-2 h-2 bg-[#13A8BD] rounded-full" />}
              </div>
              <p className="text-xs text-slate-600 ml-5">{n.message}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function UserManager({ users, setUsers, authApi, loadData, currentUser }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "admin" });

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await authApi().post("/admin/users", form);
      setForm({ email: "", password: "", name: "", role: "admin" });
      setShowNew(false);
      loadData();
      toast.success("User created.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed.");
    }
  };

  const deleteUser = async (id) => {
    await authApi().delete(`/admin/users/${id}`);
    setUsers(users.filter((u) => u.id !== id));
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowNew(!showNew)} data-testid="add-user-button" className="inline-flex items-center gap-1 bg-[#13A8BD] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-[#0e8a9c]">
          <Plus size={14} /> Add User
        </button>
      </div>
      {showNew && (
        <form onSubmit={addUser} className="bg-white border border-[#0B2545]/10 p-6 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-9 text-xs" />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none h-9 text-xs" />
          <Input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none h-9 text-xs" />
          <div className="flex gap-2">
            <Select value={form.role} onValueChange={(val) => setForm({ ...form, role: val })}>
              <SelectTrigger className="rounded-none h-9 text-xs flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["superadmin", "admin", "auditor", "viewer"].map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <button type="submit" className="bg-[#0B2545] text-white text-xs font-bold px-4 h-9">Save</button>
          </div>
        </form>
      )}
      <div className="bg-white border border-[#0B2545]/10">
        {users.map((u) => (
          <div key={u.id} className="p-4 border-b border-[#0B2545]/10 last:border-b-0 flex items-center justify-between hover:bg-[#F7F9FC]">
            <div>
              <p className="text-sm font-bold text-[#0B2545]">{u.name}</p>
              <p className="text-[10px] text-slate-500">{u.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold px-2 py-0.5 uppercase ${u.role === 'superadmin' ? 'bg-purple-50 text-purple-700' : u.role === 'admin' ? 'bg-blue-50 text-blue-700' : u.role === 'auditor' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{u.role}</span>
              <span className={`text-[10px] px-2 py-0.5 ${u.active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>{u.active ? 'Active' : 'Inactive'}</span>
              {u.email !== currentUser?.email && <button onClick={() => deleteUser(u.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={12} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
